import type { UseQueryResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Building2, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useApiProblem, type ApiError, type OperationsDayResponse } from '@/shared/api';
import { Button, EmptyState } from '@/shared/components';
import { useObjectScope } from '@/shared/object-scope';
import { heuteProblemText, isFinalProblem } from './heuteProblem';

const card = 'border-hairline shadow-float rounded-tile border bg-white';

function Alert({ text, action }: { text: string; action?: ReactNode }) {
  return (
    <div role="alert" className={`${card} flex flex-wrap items-center gap-3 p-4`}>
      <TriangleAlert size={18} aria-hidden className="text-amber flex-none" />
      <p className="min-w-0 flex-1 text-sm">{text}</p>
      {action}
    </div>
  );
}

/** Ohne gewähltes Objekt: Liste kam nicht an, es gibt keins, oder eins ist zu wählen. */
function NoObject() {
  const scope = useObjectScope();
  const navigate = useNavigate();
  const problem = useApiProblem(scope.error);
  if (scope.isError || scope.isOffline) {
    const reason = scope.isOffline
      ? ' Keine Verbindung.'
      : problem
        ? ` ${problem.detail ?? problem.title}`
        : '';
    const saved = scope.savedGroupId ? ' Ihre gespeicherte Auswahl greift gerade nicht.' : '';
    return (
      <Alert
        text={`Die Objektliste kam nicht an.${reason}${saved}`}
        action={
          <Button variant="secondary" onClick={scope.retry}>
            Erneut laden
          </Button>
        }
      />
    );
  }
  const first = scope.groups[0];
  return (
    <div className={card}>
      <EmptyState
        icon="Building"
        title={
          scope.isLoading
            ? 'Objekte werden geladen'
            : first
              ? 'Wählen Sie ein Objekt'
              : 'Noch kein Objekt'
        }
        description="Die Tagesübersicht gilt immer für genau ein Objekt und seine lokale Zeit."
        action={
          first
            ? {
                label: `${first.name} öffnen`,
                onClick: () => scope.setGroupId(first.id),
                leftIcon: <Building2 size={16} />,
              }
            : scope.isLoading
              ? undefined
              : {
                  label: 'Zu den Objekten',
                  onClick: () => void navigate({ to: '/property-groups' }),
                }
        }
      />
    </div>
  );
}

/**
 * Zustände von Heute ohne fertige Daten: kein Objekt, lädt, Fehler mit dem Grund des Servers,
 * offline oder ein Stand, dessen Aktualisierung fehlschlug. Nie endlos „Lade …“, nie still leer.
 */
export function HeuteBoardState({
  board,
  problem,
  asOf,
  onRetry,
}: {
  board: UseQueryResult<OperationsDayResponse>;
  problem: ApiError | null;
  asOf: string | null;
  onRetry: () => void;
}) {
  const scope = useObjectScope();
  if (!scope.group) return <NoObject />;
  if (board.isPending && board.fetchStatus === 'fetching') {
    return <div className={`${card} text-ink-soft p-6 text-sm`}>Der Tag wird geladen …</div>;
  }
  const offline = board.fetchStatus === 'paused';
  if (!board.isError && !offline) return null;
  const final = isFinalProblem(problem);
  const stale = Boolean(board.data) && !final;
  const reason = heuteProblemText(problem);
  const text = offline
    ? stale && asOf
      ? `Keine Verbindung – du siehst den Stand von ${asOf} Uhr.`
      : 'Keine Verbindung – der Tag kann gerade nicht geladen werden.'
    : stale
      ? 'Aktualisieren fehlgeschlagen – du siehst den zuletzt geladenen Stand.'
      : 'Der Tag konnte nicht geladen werden.';
  return (
    <Alert
      text={`${text}${!offline && reason ? ` ${reason}` : ''}`}
      action={
        final ? undefined : (
          <Button variant="secondary" onClick={onRetry} loading={board.isFetching}>
            Erneut versuchen
          </Button>
        )
      }
    />
  );
}
